import React, { useState, useEffect } from 'react';
import postTemplateService, { PostTemplate, CreateTemplateData } from '../../../services/postTemplateService';
import {
  DocumentDuplicateIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  EyeIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const PostTemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<PostTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<PostTemplate | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<PostTemplate | null>(null);
  
  const [formData, setFormData] = useState<CreateTemplateData>({
    name: '',
    title: '',
    content: '',
    category: '',
    variables: [],
    tierAccess: [],
    isPublic: true,
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await postTemplateService.getAll();
      setTemplates(data.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await postTemplateService.update(editingTemplate.id, formData);
      } else {
        await postTemplateService.create(formData);
      }
      setShowModal(false);
      resetForm();
      loadTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('Failed to save template. Please try again.');
    }
  };

  const handleUseTemplate = async (id: string) => {
    try {
      await postTemplateService.use(id);
      alert('Template marked as used! The use count has been incremented.');
      loadTemplates();
    } catch (error) {
      console.error('Failed to use template:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    try {
      await postTemplateService.delete(id);
      loadTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert('Failed to delete template.');
    }
  };

  const openCreateModal = () => {
    resetForm();
    setEditingTemplate(null);
    setShowModal(true);
  };

  const openEditModal = (template: PostTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      title: template.title,
      content: template.content,
      category: template.category,
      variables: template.variables || [],
      tierAccess: template.tierAccess || [],
      isPublic: template.isPublic,
    });
    setShowModal(true);
  };

  const openPreviewModal = (template: PostTemplate) => {
    setSelectedTemplate(template);
    setShowPreviewModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      content: '',
      category: '',
      variables: [],
      tierAccess: [],
      isPublic: true,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <DocumentDuplicateIcon className="h-10 w-10 text-purple-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Post Templates</h1>
                <p className="text-gray-600 mt-1">Create reusable content templates with variables</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={loadTemplates}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
              <button
                onClick={openCreateModal}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Template
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Total Templates</p>
            <p className="text-2xl font-bold text-gray-900">{templates.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Public</p>
            <p className="text-2xl font-bold text-green-600">
              {templates.filter(t => t.isPublic).length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Total Uses</p>
            <p className="text-2xl font-bold text-blue-600">
              {templates.reduce((sum, t) => sum + (t.useCount || 0), 0)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Categories</p>
            <p className="text-2xl font-bold text-purple-600">
              {new Set(templates.map(t => t.category).filter(Boolean)).size}
            </p>
          </div>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
            <span className="ml-2 text-gray-600">Loading templates...</span>
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <DocumentDuplicateIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No templates</h3>
            <p className="mt-1 text-sm text-gray-500">Create your first post template</p>
            <div className="mt-6">
              <button
                onClick={openCreateModal}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Template
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <div
                key={template.id}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-6"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{template.name}</h3>
                    {template.category && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                        {template.category}
                      </span>
                    )}
                  </div>
                  {template.isPublic && (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" title="Public template" />
                  )}
                </div>
                
                <p className="text-sm text-gray-700 line-clamp-3 mb-4">{template.content}</p>
                
                <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                  <span>Used: {template.useCount || 0} times</span>
                  {template.variables && template.variables.length > 0 && (
                    <span>{template.variables.length} variables</span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleUseTemplate(template.id)}
                    className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
                  >
                    Use Template
                  </button>
                  <button
                    onClick={() => openPreviewModal(template)}
                    className="text-blue-600 hover:text-blue-900"
                    title="Preview"
                  >
                    <EyeIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => openEditModal(template)}
                    className="text-gray-600 hover:text-gray-900"
                    title="Edit"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(template.id)}
                    className="text-red-600 hover:text-red-900"
                    title="Delete"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)} />
              
              <div className="inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-3xl sm:w-full">
                <form onSubmit={handleSubmit}>
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {editingTemplate ? 'Edit Template' : 'Create Template'}
                    </h3>
                    
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                          <input
                            type="text"
                            required
                            value={formData.name}
                            onChange={(e) => setFormData((prev: CreateTemplateData) => ({ ...prev, name: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                            placeholder="Weekly Update"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                          <input
                            type="text"
                            value={formData.category}
                            onChange={(e) => setFormData((prev: CreateTemplateData) => ({ ...prev, category: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                            placeholder="Updates, Announcements, etc."
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                        <input
                          type="text"
                          required
                          value={formData.title}
                          onChange={(e) => setFormData((prev: CreateTemplateData) => ({ ...prev, title: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                          placeholder="This Week's Update"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
                        <textarea
                          required
                          value={formData.content}
                          onChange={(e) => setFormData((prev: CreateTemplateData) => ({ ...prev, content: e.target.value }))}
                          rows={8}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                          placeholder="Hello {{patron_name}}! This week I've been working on..."
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Use {'{{variable_name}}'} for dynamic content
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Variables (comma-separated)</label>
                        <input
                          type="text"
                          value={formData.variables?.join(', ') || ''}
                          onChange={(e) => setFormData((prev: CreateTemplateData) => ({ 
                            ...prev, 
                            variables: e.target.value.split(',').map(v => v.trim()).filter(Boolean) 
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                          placeholder="patron_name, tier_name, custom_field"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Define custom variables for replacement
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tier Access (comma-separated IDs)</label>
                        <input
                          type="text"
                          value={formData.tierAccess?.join(', ') || ''}
                          onChange={(e) => setFormData((prev: CreateTemplateData) => ({ 
                            ...prev, 
                            tierAccess: e.target.value.split(',').map(t => t.trim()).filter(Boolean) 
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                          placeholder="Leave empty for all tiers"
                        />
                      </div>

                      <div className="flex items-center">
                        <label className="flex items-center text-sm">
                          <input
                            type="checkbox"
                            checked={formData.isPublic}
                            onChange={(e) => setFormData((prev: CreateTemplateData) => ({ ...prev, isPublic: e.target.checked }))}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 mr-2"
                          />
                          <span>Public template (visible to all patrons)</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end gap-2">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                    >
                      {editingTemplate ? 'Update' : 'Create'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {showPreviewModal && selectedTemplate && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowPreviewModal(false)} />
              
              <div className="inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-2xl sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{selectedTemplate.name}</h3>
                  {selectedTemplate.category && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 mb-4">
                      {selectedTemplate.category}
                    </span>
                  )}
                  
                  <div className="mt-4 p-4 bg-gray-50 rounded-md">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedTemplate.content}</p>
                  </div>

                  {selectedTemplate.variables && selectedTemplate.variables.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Available Variables:</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedTemplate.variables.map((variable, idx) => (
                          <span key={idx} className="inline-flex items-center px-2 py-1 rounded text-xs font-mono bg-blue-100 text-blue-800">
                            {'{{' + variable + '}}'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex items-center gap-4 text-xs text-gray-500">
                    <span>Used: {selectedTemplate.useCount || 0} times</span>
                    <span>Created: {new Date(selectedTemplate.createdAt).toLocaleDateString()}</span>
                    {selectedTemplate.isPublic ? (
                      <span className="text-green-600">Public</span>
                    ) : (
                      <span className="text-gray-600">Private</span>
                    )}
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end gap-2">
                  <button
                    onClick={() => {
                      handleUseTemplate(selectedTemplate.id);
                      setShowPreviewModal(false);
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                  >
                    Use Template
                  </button>
                  <button
                    onClick={() => setShowPreviewModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostTemplatesPage;
